import { useCallback, useContext, useEffect, useState } from 'react';
import { SessionContext } from '../../context/SessionContext';
import { useCurrentSession } from '../chat/useCurrentSession';
import { Operation, messages, validateMessage } from './messagesUtils';

interface UseParentProps {
  standalone?: boolean;
  standaloneUrl?: string;
  childWidth: number;
  childHeight: number;
}

interface UseParentResult {
  childIsOpened: boolean;
  openChild: () => void;
  focusChild: () => void;
}

export const useParent = ({ childHeight, childWidth, standalone, standaloneUrl }: UseParentProps): UseParentResult => {
  const [child, setChild] = useState<Window | null>();
  const { selectSession } = useContext(SessionContext);
  const { sessionId, refetch } = useCurrentSession();

  useEffect(() => {
    if (!standalone) {
      const handler = (e: MessageEvent) => {
        const message = validateMessage(e);

        if (message) {
          switch (message.op) {
            case Operation.pingParent:
              setChild(e.source as Window);
              break;
            case Operation.childWasClosed:
              setChild(undefined);

              if (message.lastSessionId) {
                selectSession(message.lastSessionId);
                refetch();
              }
              break;
            default:
          }
        }
      };

      window.addEventListener('message', handler);

      return () => {
        window.removeEventListener('message', handler);
      };
    }

    return () => {};
  }, [standalone, selectSession, refetch]);

  useEffect(() => {
    if (!standalone && child) {
      child.postMessage(messages.selectSession(sessionId));
    }
  }, [child, standalone, sessionId]);

  const openChild = useCallback(() => {
    if (!standalone && standaloneUrl) {
      window.open(standaloneUrl, '_blank', `popup, width=${childWidth}, height=${childHeight}`);
    }
  }, [childHeight, childWidth, standaloneUrl, standalone]);

  const focusChild = useCallback(() => {
    if (!standalone && child) {
      child.focus();
    }
  }, [child, standalone]);

  return {
    childIsOpened: !!child,
    openChild,
    focusChild,
  };
};
